import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import * as Yup from "yup";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";

import BreadCrumb from "../../Components/Common/BreadCrumb";
import HelpArticleLink from "../../Components/Common/HelpArticleLink";
import { clearWelcomeProgress } from "../../Components/Studio/Onboarding/FirstExperienceModal";
import CopyAnamnesisToServicesModal from "../../Components/Studio/CopyAnamnesisToServicesModal";
import { HELP_ARTICLE_SETTINGS_GENERAL } from "../../helpers/helpCenter";
import { userHasAnyRole } from "../../services/authService";
import {
  getStatus,
  replayWelcome,
  restoreQuickstart,
  restoreUsageTips,
  QuickstartStatus,
  UsageTipsStatus,
} from "../../services/onboardingService";
import {
  DEFAULT_TENANT_SETTINGS,
  getSettings,
  TenantSettings,
  updateSettings,
} from "../../services/tenantSettingService";
import {
  mapSettingsToRegionalFormValues,
  RegionalSettingsFormValues,
  TENANT_CURRENCY_OPTIONS,
  TENANT_LOCALE_OPTIONS,
  TENANT_TIMEZONE_OPTIONS,
} from "./regionalSettingsOptions";
import InstagramBioLinkCard from "./InstagramBioLinkCard";
import PublicProfileCard from "./PublicProfileCard";

const GeneralSettings = () => {
  const { t } = useTranslation();

  const [settings, setSettings] = useState<TenantSettings>(DEFAULT_TENANT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [updatingToggle, setUpdatingToggle] = useState(false);
  const [updatingRestrictToggle, setUpdatingRestrictToggle] = useState(false);
  const [updatingAnamnesisScope, setUpdatingAnamnesisScope] = useState(false);
  const [updatingClinicalGateMode, setUpdatingClinicalGateMode] = useState(false);
  const [updatingBlockExpiredInventory, setUpdatingBlockExpiredInventory] = useState(false);
  const [retentionDraft, setRetentionDraft] = useState("");
  const [updatingRetention, setUpdatingRetention] = useState(false);
  const [imageConsentExpiresDraft, setImageConsentExpiresDraft] = useState("");
  const [updatingImageConsentExpires, setUpdatingImageConsentExpires] = useState(false);
  const [copyWizardOpen, setCopyWizardOpen] = useState(false);
  const [quickstart, setQuickstart] = useState<QuickstartStatus | null>(null);
  const [quickstartLoading, setQuickstartLoading] = useState(false);
  const [restoringQuickstart, setRestoringQuickstart] = useState(false);
  const [usageTips, setUsageTips] = useState<UsageTipsStatus | null>(null);
  const [restoringUsageTips, setRestoringUsageTips] = useState(false);
  const [welcomeShowing, setWelcomeShowing] = useState(false);
  const [replayingWelcome, setReplayingWelcome] = useState(false);
  const canManageQuickstart = userHasAnyRole(["owner"]);

  useEffect(() => {
    document.title = t("settings.general.pageTitle");
  }, [t]);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getSettings();
      setSettings(response.data);
      const retention = response.data.clinical_record_retention_days;
      setRetentionDraft(
        retention === null || retention === undefined ? "" : String(retention)
      );
      const imageExpires = response.data.image_marketing_consent_expires_days;
      setImageConsentExpiresDraft(
        imageExpires === null || imageExpires === undefined ? "" : String(imageExpires)
      );
    } catch (error: any) {
      toast.error(error?.data || t("settings.general.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const loadQuickstart = useCallback(async () => {
    if (!canManageQuickstart) {
      setQuickstart(null);
      setUsageTips(null);
      setWelcomeShowing(false);
      return;
    }

    setQuickstartLoading(true);

    try {
      const response = await getStatus();
      setQuickstart(response.data.quickstart);
      setUsageTips(response.data.usage_tips ?? null);
      setWelcomeShowing(response.data.welcome?.show === true);
    } catch {
      setQuickstart(null);
      setUsageTips(null);
      setWelcomeShowing(false);
    } finally {
      setQuickstartLoading(false);
    }
  }, [canManageQuickstart]);

  useEffect(() => {
    void loadQuickstart();
  }, [loadQuickstart]);

  const handleRestoreQuickstart = async () => {
    setRestoringQuickstart(true);

    try {
      const response = await restoreQuickstart();
      setQuickstart(response.data.quickstart);
      toast.success(
        <span>
          {t("onboarding.quickstart.toast.restoreSuccess")}{" "}
          <Link
            to="/dashboard"
            className="text-white text-decoration-underline fw-semibold"
          >
            {t("onboarding.quickstart.toast.restoreGoDashboard")}
          </Link>
        </span>
      );
    } catch (error: any) {
      toast.error(error?.data || t("onboarding.quickstart.toast.restoreError"));
    } finally {
      setRestoringQuickstart(false);
    }
  };

  const handleRestoreUsageTips = async () => {
    setRestoringUsageTips(true);

    try {
      const response = await restoreUsageTips();
      setUsageTips(response.data.usage_tips);
      toast.success(
        <span>
          {t("onboarding.firstExperience.toast.restoreSuccess")}{" "}
          <Link
            to="/dashboard"
            className="text-white text-decoration-underline fw-semibold"
          >
            {t("onboarding.firstExperience.toast.restoreGoDashboard")}
          </Link>
        </span>
      );
    } catch (error: any) {
      toast.error(error?.data || t("onboarding.firstExperience.toast.restoreError"));
    } finally {
      setRestoringUsageTips(false);
    }
  };

  const handleReplayWelcome = async () => {
    setReplayingWelcome(true);

    try {
      clearWelcomeProgress();
      await replayWelcome();
      setWelcomeShowing(true);
      toast.success(
        <span>
          {t("onboarding.firstExperience.toast.replaySuccess")}{" "}
          <Link
            to="/dashboard"
            className="text-white text-decoration-underline fw-semibold"
          >
            {t("onboarding.firstExperience.toast.restoreGoDashboard")}
          </Link>
        </span>
      );
    } catch (error: any) {
      toast.error(error?.data || t("onboarding.firstExperience.toast.replayError"));
    } finally {
      setReplayingWelcome(false);
    }
  };

  const regionalValidationSchema = useMemo(
    () =>
      Yup.object({
        locale: Yup.string().required(t("settings.general.validation.localeRequired")),
        timezone: Yup.string().required(t("settings.general.validation.timezoneRequired")),
        currency_code: Yup.string().required(t("settings.general.validation.currencyRequired")),
      }),
    [t]
  );

  const regionalForm = useFormik<RegionalSettingsFormValues>({
    enableReinitialize: true,
    initialValues: mapSettingsToRegionalFormValues(settings),
    validationSchema: regionalValidationSchema,
    onSubmit: async (values, helpers) => {
      try {
        const response = await updateSettings({
          locale: values.locale,
          timezone: values.timezone,
          currency_code: values.currency_code,
        });
        setSettings(response.data);
        toast.success(response.message);
      } catch (error: any) {
        toast.error(error?.data || t("settings.general.toast.saveError"));
      } finally {
        helpers.setSubmitting(false);
      }
    },
  });

  const handleCatalogToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.checked;
    setUpdatingToggle(true);

    try {
      const response = await updateSettings({
        use_catalog_categories: nextValue,
      });
      setSettings(response.data);
      toast.success(response.message);
    } catch (error: any) {
      toast.error(error?.data || t("settings.general.toast.saveError"));
    } finally {
      setUpdatingToggle(false);
    }
  };

  const handleRestrictServicesToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.checked;
    setUpdatingRestrictToggle(true);

    try {
      const response = await updateSettings({
        restrict_professional_services: nextValue,
      });
      setSettings(response.data);
      toast.success(response.message);
    } catch (error: any) {
      toast.error(error?.data || t("settings.general.toast.saveError"));
    } finally {
      setUpdatingRestrictToggle(false);
    }
  };

  const handleSaveRetention = async () => {
    const trimmed = retentionDraft.trim();
    let payloadValue: number | null = null;

    if (trimmed !== "") {
      const parsed = Number.parseInt(trimmed, 10);

      if (!Number.isFinite(parsed) || parsed < 1) {
        toast.error(t("settings.general.anamnesis.retentionInvalid"));
        return;
      }

      payloadValue = parsed;
    }

    setUpdatingRetention(true);

    try {
      const response = await updateSettings({
        clinical_record_retention_days: payloadValue,
      });
      setSettings(response.data);
      setRetentionDraft(
        response.data.clinical_record_retention_days === null
          ? ""
          : String(response.data.clinical_record_retention_days)
      );
      toast.success(response.message || t("settings.general.toast.saveSuccess"));
    } catch (error: unknown) {
      const apiError = error as { data?: string };
      toast.error(apiError.data || t("settings.general.toast.saveError"));
    } finally {
      setUpdatingRetention(false);
    }
  };

  const handleSaveImageConsentExpires = async () => {
    const trimmed = imageConsentExpiresDraft.trim();
    let payloadValue: number | null = null;

    if (trimmed !== "") {
      const parsed = Number.parseInt(trimmed, 10);

      if (!Number.isFinite(parsed) || parsed < 1) {
        toast.error(t("settings.general.anamnesis.imageConsentExpiresInvalid"));
        return;
      }

      payloadValue = parsed;
    }

    setUpdatingImageConsentExpires(true);

    try {
      const response = await updateSettings({
        image_marketing_consent_expires_days: payloadValue,
      });
      setSettings(response.data);
      setImageConsentExpiresDraft(
        response.data.image_marketing_consent_expires_days === null
          ? ""
          : String(response.data.image_marketing_consent_expires_days)
      );
      toast.success(response.message || t("settings.general.toast.saveSuccess"));
    } catch (error: unknown) {
      const apiError = error as { data?: string };
      toast.error(apiError.data || t("settings.general.toast.saveError"));
    } finally {
      setUpdatingImageConsentExpires(false);
    }
  };

  const handleAnamnesisScopeToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextScope = event.target.checked ? "per_client_service" : "per_client";

    if (nextScope === "per_client_service") {
      const confirmation = await Swal.fire({
        title: t("settings.general.anamnesis.confirmTitle"),
        text: t("settings.general.anamnesis.confirmEnable"),
        icon: "question",
        showCancelButton: true,
        confirmButtonText: t("settings.general.anamnesis.confirmContinue"),
        cancelButtonText: t("settings.general.anamnesis.confirmCancel"),
        confirmButtonColor: "#0ab39c",
        cancelButtonColor: "#878a99",
        focusCancel: true,
      });

      if (!confirmation.isConfirmed) {
        return;
      }
    }

    setUpdatingAnamnesisScope(true);

    try {
      const response = await updateSettings({
        anamnesis_scope: nextScope,
      });
      setSettings(response.data);
      toast.success(response.message);

      if (nextScope === "per_client_service") {
        const offer = await Swal.fire({
          title: t("settings.general.anamnesis.wizardOfferTitle"),
          text: t("settings.general.anamnesis.wizardOfferText"),
          icon: "success",
          showCancelButton: true,
          confirmButtonText: t("settings.general.anamnesis.wizardOfferConfirm"),
          cancelButtonText: t("settings.general.anamnesis.wizardOfferLater"),
          confirmButtonColor: "#0ab39c",
          cancelButtonColor: "#878a99",
        });

        if (offer.isConfirmed) {
          setCopyWizardOpen(true);
        }
      }
    } catch (error: any) {
      toast.error(error?.data || t("settings.general.toast.saveError"));
    } finally {
      setUpdatingAnamnesisScope(false);
    }
  };

  const handleClinicalGateModeToggle = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nextMode = event.target.checked ? "soft_block" : "warn";
    setUpdatingClinicalGateMode(true);

    try {
      const response = await updateSettings({
        clinical_gate_mode: nextMode,
      });
      setSettings(response.data);
      toast.success(response.message);
    } catch (error: any) {
      toast.error(error?.data || t("settings.general.toast.saveError"));
    } finally {
      setUpdatingClinicalGateMode(false);
    }
  };

  const handleBlockExpiredInventoryToggle = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nextValue = event.target.checked;
    setUpdatingBlockExpiredInventory(true);

    try {
      const response = await updateSettings({
        block_expired_inventory: nextValue,
      });
      setSettings(response.data);
      toast.success(response.message);
    } catch (error: any) {
      toast.error(error?.data || t("settings.general.toast.saveError"));
    } finally {
      setUpdatingBlockExpiredInventory(false);
    }
  };

  return (
    <div className="page-content">
      <ToastContainer closeButton pauseOnHover />
      <Container fluid>
        <BreadCrumb
          title={t("settings.general.breadcrumb.title")}
          pageTitle={t("settings.general.breadcrumb.pageTitle")}
        />
        <div className="d-flex justify-content-end mb-3">
          <HelpArticleLink articlePath={HELP_ARTICLE_SETTINGS_GENERAL} />
        </div>
        <Row className="justify-content-center">
          <Col lg={8} xl={7}>
            <Card className="mb-4">
              <CardHeader>
                <h5 className="card-title mb-0">{t("settings.general.regional.title")}</h5>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="d-flex justify-content-center py-3">
                    <Spinner color="primary" size="sm" />
                  </div>
                ) : (
                  <Form
                    onSubmit={(event) => {
                      event.preventDefault();
                      regionalForm.handleSubmit();
                    }}
                  >
                    <Row className="g-3">
                      <Col md={4}>
                        <Label htmlFor="settings-locale" className="form-label">
                          {t("settings.general.regional.locale")}
                        </Label>
                        <Input
                          id="settings-locale"
                          name="locale"
                          type="select"
                          value={regionalForm.values.locale}
                          onChange={regionalForm.handleChange}
                          onBlur={regionalForm.handleBlur}
                          invalid={!!(regionalForm.touched.locale && regionalForm.errors.locale)}
                        >
                          {TENANT_LOCALE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {t(option.labelKey)}
                            </option>
                          ))}
                        </Input>
                        {regionalForm.touched.locale && regionalForm.errors.locale ? (
                          <FormFeedback type="invalid">{regionalForm.errors.locale}</FormFeedback>
                        ) : null}
                      </Col>
                      <Col md={4}>
                        <Label htmlFor="settings-timezone" className="form-label">
                          {t("settings.general.regional.timezone")}
                        </Label>
                        <Input
                          id="settings-timezone"
                          name="timezone"
                          type="select"
                          value={regionalForm.values.timezone}
                          onChange={regionalForm.handleChange}
                          onBlur={regionalForm.handleBlur}
                          invalid={!!(regionalForm.touched.timezone && regionalForm.errors.timezone)}
                        >
                          {TENANT_TIMEZONE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {t(option.labelKey)}
                            </option>
                          ))}
                        </Input>
                        {regionalForm.touched.timezone && regionalForm.errors.timezone ? (
                          <FormFeedback type="invalid">{regionalForm.errors.timezone}</FormFeedback>
                        ) : null}
                      </Col>
                      <Col md={4}>
                        <Label htmlFor="settings-currency" className="form-label">
                          {t("settings.general.regional.currency")}
                        </Label>
                        <Input
                          id="settings-currency"
                          name="currency_code"
                          type="select"
                          value={regionalForm.values.currency_code}
                          onChange={regionalForm.handleChange}
                          onBlur={regionalForm.handleBlur}
                          invalid={
                            !!(regionalForm.touched.currency_code && regionalForm.errors.currency_code)
                          }
                        >
                          {TENANT_CURRENCY_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {t(option.labelKey)}
                            </option>
                          ))}
                        </Input>
                        {regionalForm.touched.currency_code && regionalForm.errors.currency_code ? (
                          <FormFeedback type="invalid">{regionalForm.errors.currency_code}</FormFeedback>
                        ) : null}
                      </Col>
                    </Row>
                    <p className="text-muted small mb-3 mt-3">
                      {t("settings.general.regional.help")}
                    </p>
                    <Button
                      type="submit"
                      color="primary"
                      disabled={regionalForm.isSubmitting || !regionalForm.dirty}
                    >
                      {regionalForm.isSubmitting ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          {t("settings.general.actions.saving")}
                        </>
                      ) : (
                        t("settings.general.actions.saveRegional")
                      )}
                    </Button>
                  </Form>
                )}
              </CardBody>
            </Card>

            <Card className="mb-4">
              <CardHeader>
                <h5 className="card-title mb-0">{t("settings.general.catalog.title")}</h5>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="d-flex justify-content-center py-3">
                    <Spinner color="primary" size="sm" />
                  </div>
                ) : (
                  <>
                    <div className="form-check form-switch mb-3">
                      <Input
                        id="use-catalog-categories"
                        type="checkbox"
                        role="switch"
                        className="form-check-input"
                        checked={settings.use_catalog_categories}
                        disabled={updatingToggle}
                        onChange={handleCatalogToggle}
                      />
                      <Label className="form-check-label" htmlFor="use-catalog-categories">
                        {t("settings.general.catalog.toggleLabel")}
                      </Label>
                    </div>
                    <p className="text-muted small mb-3">
                      {t("settings.general.catalog.toggleHelp")}
                    </p>
                    {settings.use_catalog_categories ? (
                      <Link to="/settings/catalog-categories" className="btn btn-soft-primary btn-sm">
                        <i className="ri-price-tag-3-line align-bottom me-1" />
                        {t("settings.general.catalog.manageLink")}
                      </Link>
                    ) : null}
                  </>
                )}
              </CardBody>
            </Card>

            <Card className="mb-4">
              <CardHeader>
                <h5 className="card-title mb-0">{t("settings.general.anamnesis.title")}</h5>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="d-flex justify-content-center py-3">
                    <Spinner color="primary" size="sm" />
                  </div>
                ) : (
                  <>
                    <div className="form-check form-switch mb-3">
                      <Input
                        id="anamnesis-scope-per-service"
                        type="checkbox"
                        role="switch"
                        className="form-check-input"
                        checked={settings.anamnesis_scope === "per_client_service"}
                        disabled={updatingAnamnesisScope}
                        onChange={handleAnamnesisScopeToggle}
                      />
                      <Label className="form-check-label" htmlFor="anamnesis-scope-per-service">
                        {t("settings.general.anamnesis.toggleLabel")}
                      </Label>
                    </div>
                    <p className="text-muted small mb-3">
                      {t("settings.general.anamnesis.toggleHelp")}
                    </p>
                    <div className="form-check form-switch mb-3">
                      <Input
                        id="clinical-gate-soft-block"
                        type="checkbox"
                        role="switch"
                        className="form-check-input"
                        checked={settings.clinical_gate_mode === "soft_block"}
                        disabled={updatingClinicalGateMode}
                        onChange={handleClinicalGateModeToggle}
                      />
                      <Label className="form-check-label" htmlFor="clinical-gate-soft-block">
                        {t("settings.general.anamnesis.clinicalGateSoftBlockLabel")}
                      </Label>
                    </div>
                    <p className="text-muted small mb-3">
                      {t("settings.general.anamnesis.clinicalGateSoftBlockHelp")}
                    </p>
                    <div className="form-check form-switch mb-3">
                      <Input
                        id="block-expired-inventory"
                        type="checkbox"
                        role="switch"
                        className="form-check-input"
                        checked={settings.block_expired_inventory ?? false}
                        disabled={updatingBlockExpiredInventory}
                        onChange={handleBlockExpiredInventoryToggle}
                      />
                      <Label className="form-check-label" htmlFor="block-expired-inventory">
                        {t("settings.general.inventory.blockExpiredLabel")}
                      </Label>
                    </div>
                    <p className="text-muted small mb-3">
                      {t("settings.general.inventory.blockExpiredHelp")}
                    </p>
                    {settings.anamnesis_scope === "per_client_service" ? (
                      <Alert color="warning" className="mb-3">
                        <p className="mb-2">{t("settings.general.anamnesis.warning")}</p>
                        <div className="d-flex flex-wrap gap-2 align-items-center">
                          <Button
                            color="warning"
                            size="sm"
                            type="button"
                            outline
                            onClick={() => setCopyWizardOpen(true)}
                          >
                            <i className="ri-file-copy-2-line me-1" />
                            {t("settings.general.anamnesis.openCopyWizard")}
                          </Button>
                          <Link to="/services" className="alert-link">
                            {t("settings.general.anamnesis.linkServices")}
                          </Link>
                          <Link to="/settings/anamnesis-templates" className="alert-link">
                            {t("settings.general.anamnesis.linkTemplates")}
                          </Link>
                        </div>
                      </Alert>
                    ) : null}
                    <div className="border-top pt-3 mt-2">
                      <Label htmlFor="clinical-record-retention-days" className="form-label">
                        {t("settings.general.anamnesis.retentionLabel")}
                      </Label>
                      <div className="d-flex flex-wrap gap-2 align-items-center">
                        <Input
                          id="clinical-record-retention-days"
                          type="number"
                          min={1}
                          className="form-control"
                          style={{ maxWidth: 160 }}
                          placeholder={t("settings.general.anamnesis.retentionPlaceholder")}
                          value={retentionDraft}
                          disabled={updatingRetention}
                          onChange={(event) => setRetentionDraft(event.target.value)}
                        />
                        <Button
                          color="primary"
                          size="sm"
                          type="button"
                          disabled={updatingRetention}
                          onClick={() => void handleSaveRetention()}
                        >
                          {updatingRetention ? (
                            <Spinner size="sm" className="me-1" />
                          ) : null}
                          {t("settings.general.anamnesis.retentionSave")}
                        </Button>
                      </div>
                      <p className="text-muted small mb-0 mt-2">
                        {t("settings.general.anamnesis.retentionHelp")}
                      </p>
                      {retentionDraft.trim() !== "" ? (
                        <Alert color="warning" className="mt-2 mb-0 py-2">
                          {t("settings.general.anamnesis.retentionWarning")}
                        </Alert>
                      ) : null}
                    </div>
                    <div className="border-top pt-3 mt-3">
                      <Label
                        htmlFor="image-marketing-consent-expires-days"
                        className="form-label"
                      >
                        {t("settings.general.anamnesis.imageConsentExpiresLabel")}
                      </Label>
                      <div className="d-flex flex-wrap gap-2 align-items-center">
                        <Input
                          id="image-marketing-consent-expires-days"
                          type="number"
                          min={1}
                          className="form-control"
                          style={{ maxWidth: 160 }}
                          placeholder={t(
                            "settings.general.anamnesis.imageConsentExpiresPlaceholder"
                          )}
                          value={imageConsentExpiresDraft}
                          disabled={updatingImageConsentExpires}
                          onChange={(event) =>
                            setImageConsentExpiresDraft(event.target.value)
                          }
                        />
                        <Button
                          color="primary"
                          size="sm"
                          type="button"
                          disabled={updatingImageConsentExpires}
                          onClick={() => void handleSaveImageConsentExpires()}
                        >
                          {updatingImageConsentExpires ? (
                            <Spinner size="sm" className="me-1" />
                          ) : null}
                          {t("settings.general.anamnesis.imageConsentExpiresSave")}
                        </Button>
                      </div>
                      <p className="text-muted small mb-0 mt-2">
                        {t("settings.general.anamnesis.imageConsentExpiresHelp")}
                      </p>
                    </div>
                  </>
                )}
              </CardBody>
            </Card>

            <InstagramBioLinkCard bookingToken={settings.booking_token} />

            {canManageQuickstart ? (
              <Card className="mb-4">
                <CardHeader>
                  <h5 className="card-title mb-0">{t("onboarding.quickstart.settings.title")}</h5>
                </CardHeader>
                <CardBody>
                  {quickstartLoading ? (
                    <div className="d-flex align-items-center gap-2">
                      <Spinner size="sm" color="primary" />
                      <span className="text-muted mb-0">{t("onboarding.quickstart.loading")}</span>
                    </div>
                  ) : quickstart?.dismissed ? (
                    <>
                      <p className="text-muted mb-3">
                        {t("onboarding.quickstart.settings.dismissedHelp")}
                      </p>
                      <Button
                        color="success"
                        size="sm"
                        onClick={() => void handleRestoreQuickstart()}
                        disabled={restoringQuickstart}
                      >
                        {restoringQuickstart ? (
                          <Spinner size="sm" className="me-1" />
                        ) : (
                          <i className="ri-flashlight-line me-1" />
                        )}
                        {t("onboarding.quickstart.actions.restore")}
                      </Button>
                    </>
                  ) : (
                    <>
                      <p className="text-muted mb-2">
                        {t("onboarding.quickstart.settings.visibleHelp", {
                          done: quickstart?.completed_count ?? 0,
                          total: quickstart?.total ?? 3,
                        })}
                      </p>
                      <Link to="/dashboard" className="btn btn-soft-success btn-sm">
                        <i className="ri-dashboard-line me-1" />
                        {t("onboarding.quickstart.settings.openDashboard")}
                      </Link>
                    </>
                  )}
                </CardBody>
              </Card>
            ) : null}

            {canManageQuickstart ? (
              <Card className="mb-4">
                <CardHeader>
                  <h5 className="card-title mb-0">
                    {t("onboarding.banner.settings.title")}
                  </h5>
                </CardHeader>
                <CardBody>
                  {quickstartLoading ? (
                    <div className="d-flex align-items-center gap-2">
                      <Spinner size="sm" color="primary" />
                      <span className="text-muted mb-0">
                        {t("onboarding.banner.loading")}
                      </span>
                    </div>
                  ) : (
                    <>
                      <p className="text-muted mb-2">
                        {t("onboarding.banner.settings.visibleHelp")}
                      </p>
                      <Link
                        to="/dashboard?concierge=1"
                        className="btn btn-soft-primary btn-sm"
                      >
                        <i className="ri-calendar-schedule-line me-1" />
                        {t("onboarding.actions.bookSetup")}
                      </Link>
                    </>
                  )}
                </CardBody>
              </Card>
            ) : null}

            {canManageQuickstart ? (
              <Card className="mb-4">
                <CardHeader>
                  <h5 className="card-title mb-0">
                    {t("onboarding.firstExperience.settings.title")}
                  </h5>
                </CardHeader>
                <CardBody>
                  {quickstartLoading ? (
                    <div className="d-flex align-items-center gap-2">
                      <Spinner size="sm" color="primary" />
                      <span className="text-muted mb-0">
                        {t("onboarding.firstExperience.loading")}
                      </span>
                    </div>
                  ) : usageTips?.dismissed ? (
                    <>
                      <p className="text-muted mb-3">
                        {t("onboarding.firstExperience.settings.dismissedHelp")}
                      </p>
                      <Button
                        color="primary"
                        size="sm"
                        onClick={() => void handleRestoreUsageTips()}
                        disabled={restoringUsageTips}
                      >
                        {restoringUsageTips ? (
                          <Spinner size="sm" className="me-1" />
                        ) : (
                          <i className="ri-lightbulb-line me-1" />
                        )}
                        {t("onboarding.firstExperience.actions.restore")}
                      </Button>
                    </>
                  ) : (
                    <>
                      <p className="text-muted mb-2">
                        {t("onboarding.firstExperience.settings.visibleHelp")}
                      </p>
                      <Link to="/dashboard" className="btn btn-soft-primary btn-sm">
                        <i className="ri-dashboard-line me-1" />
                        {t("onboarding.firstExperience.settings.openDashboard")}
                      </Link>
                    </>
                  )}
                  <hr className="my-3" />
                  {welcomeShowing ? (
                    <p className="text-muted mb-0">
                      {t("onboarding.firstExperience.settings.welcomeOnHome")}
                    </p>
                  ) : (
                    <>
                      <p className="text-muted mb-2">
                        {t("onboarding.firstExperience.settings.replayHelp")}
                      </p>
                      <Button
                        color="light"
                        size="sm"
                        onClick={() => void handleReplayWelcome()}
                        disabled={replayingWelcome}
                      >
                        {replayingWelcome ? (
                          <Spinner size="sm" className="me-1" />
                        ) : (
                          <i className="ri-play-circle-line me-1" />
                        )}
                        {t("onboarding.firstExperience.actions.replay")}
                      </Button>
                    </>
                  )}
                </CardBody>
              </Card>
            ) : null}

            <PublicProfileCard />

            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">{t("studio.settings.general.enablement.title")}</h5>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="d-flex justify-content-center py-3">
                    <Spinner color="primary" size="sm" />
                  </div>
                ) : (
                  <>
                    <div className="form-check form-switch mb-3">
                      <Input
                        id="restrict-professional-services"
                        type="checkbox"
                        role="switch"
                        className="form-check-input"
                        checked={settings.restrict_professional_services}
                        disabled={updatingRestrictToggle}
                        onChange={handleRestrictServicesToggle}
                      />
                      <Label className="form-check-label" htmlFor="restrict-professional-services">
                        {t("studio.settings.general.enablement.toggleLabel")}
                      </Label>
                    </div>
                    <p className="text-muted small mb-3">
                      {t("studio.settings.general.enablement.toggleHelp")}
                    </p>
                    {settings.restrict_professional_services ? (
                      <Alert color="warning" className="mb-3">
                        <p className="mb-2">{t("studio.settings.general.enablement.warning")}</p>
                        <Link to="/services" className="alert-link">
                          {t("studio.settings.general.enablement.reviewLink")}
                        </Link>
                      </Alert>
                    ) : null}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <CopyAnamnesisToServicesModal
        isOpen={copyWizardOpen}
        onClose={() => setCopyWizardOpen(false)}
      />
    </div>
  );
};

export default GeneralSettings;
